import React, { useState, useMemo } from 'react';
import { GridSortModel } from '@mui/x-data-grid';
import { useSnackbar } from 'notistack';
import { Button, Grid, IconButton } from '@mui/material';
import { Icon } from '@iconify/react';
import fileOutline from '@iconify-icons/eva/file-outline';
import trash2outline from '@iconify-icons/eva/trash-2-outline';
import { useTheme } from '@emotion/react';
import { get } from 'lodash';
import VisibilityIcon from '@mui/icons-material/Visibility';
import emailOutline from '@iconify-icons/eva/email-outline';
import { CloseIcon } from '@/common/icons/CloseIcon';
import { MIconButton } from '@/@material-extend';
import axios from 'axios';

import {
  getOrderBy,
  mergeCommonGridColConfig,
  PAGE_SIZES,
  renderDateTimeFromCol
} from '@/RestoreComponent/DataGrid/utils';
import { RestoreDataGrid } from '@/RestoreComponent/DataGrid';
import { callRestGetEndpoint } from '@/common/utils/api';
import UploadFileModal from './UploadFileModal';
import { isAtLeastManager } from '@/models/common';
import AuthUserStore from '../../../../../../../common/utils/AuthUser';
import { ConfirmationDialog } from '@/RestoreComponent/ConfirmationDialog';
import CONFIG from '@/config';
import UploadedDocumentView from './UploadDocumentView';

const getQueryParams = (
  customerAllData,
  sortOptions,
  pageSize,
  page,
  pageInfo
) => {
  const params = {
    customer: customerAllData?.id,
    orderBy: getOrderBy(sortOptions),
    first: pageSize
  };
  if (page != 0 && pageInfo?.endCursor) {
    params['after'] = pageInfo.endCursor;
  }
  return params;
};

const UploadedDocuments = (props: any) => {
  const { customerAllData } = props;

  const theme = useTheme();
  const { enqueueSnackbar, closeSnackbar } = useSnackbar();

  const [documents, setDocuments] = useState([]);
  const [uploadModalOpen, setUploadModalOpen] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [itemContext, setItemContext] = useState(null);
  const [viewDocumentOpen, setViewDocumentOpen] = useState(false);

  const isManager = isAtLeastManager(AuthUserStore.getRoles());

  // datagrid state:
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(PAGE_SIZES[0]);
  const [pageInfo, setPageInfo] = useState({
    endCursor: undefined
  });
  const initialSort: GridSortModel = [{ field: 'created_at', sort: 'asc' }];
  const [sortOptions, setSortOptions] = useState(initialSort);
  const [totalCount, setTotalCount] = useState(0);

  const columnConfig = useMemo(
    () => [
      mergeCommonGridColConfig({
        field: 'created_at',
        headerName: 'Upload Date',
        type: 'date',
        flex: 1,
        valueGetter: renderDateTimeFromCol('created_at')
      }),
      mergeCommonGridColConfig({
        field: 'filename',
        headerName: 'File Name',
        type: 'string',
        flex: 3
      }),
      mergeCommonGridColConfig({
        field: 'mimetype',
        headerName: 'Type',
        type: 'string',
        flex: 1
      }),
      mergeCommonGridColConfig({
        field: 'action',
        headerName: 'Actions',
        type: 'actions',
        description: 'View or refund a purchase',
        flex: 1.4,
        sortable: false,
        headerAlign: 'center',
        renderCell: p => {
          const restoreBlue = get(theme, 'palette.primary["restoreBlue"]');
          return (
            <>
              <Grid container ml={3} sx={{ justifyContent: 'space-around' }}>
                <IconButton
                  size="small"
                  onClick={() => openDocument(p?.row?.id)}
                >
                  <Icon icon={fileOutline} color={restoreBlue} />
                </IconButton>
                <IconButton
                  size="small"
                  onClick={() => sendViaEmail(p?.row?.id)}
                >
                  <Icon icon={emailOutline} color={restoreBlue} />
                </IconButton>
                {isManager && (
                  <>
                    &nbsp;&nbsp;
                    <IconButton
                      size="small"
                      onClick={() => {
                        setItemContext(p?.row?.id);
                        setShowConfirm(true);
                      }}
                    >
                      <Icon icon={trash2outline} color={restoreBlue} />
                    </IconButton>
                  </>
                )}
              </Grid>
            </>
          );
        }
      })
    ],
    []
  );

  const openDocument = id => {
    setItemContext(id);
    setViewDocumentOpen(true);
  };

  const sendViaEmail = async id => {
    setLoading(true);
    try {
      await axios.post(
        CONFIG.API + '/pos_customer/customer_upload_document_email/',
        {
          document_id: id,
          customer_id: customerAllData.id
        },
        {
          headers: {
            Authorization:
              'JWT ' + (localStorage.getItem('RES_AUTH_TOKEN') || '')
          }
        }
      );
      enqueueSnackbar(`Document emailed.`, {
        variant: 'success',
        action: (key: any) => (
          <MIconButton size="small" onClick={() => closeSnackbar(key)}>
            <CloseIcon />
          </MIconButton>
        )
      });
    } catch (e) {
      enqueueSnackbar('A problem was encountered sending the document.', {
        variant: 'error'
      });
    } finally {
      setLoading(false);
    }
  };

  const deleteFile = async () => {
    setLoading(true);
    try {
      await axios.post(
        CONFIG.API + '/pos_customer/customer_upload_document_delete/',
        {
          document_id: itemContext
        },
        {
          headers: {
            Authorization:
              'JWT ' + (localStorage.getItem('RES_AUTH_TOKEN') || '')
          }
        }
      );
      enqueueSnackbar(`File deleted.`, {
        variant: 'success',
        action: (key: any) => (
          <MIconButton size="small" onClick={() => closeSnackbar(key)}>
            <CloseIcon />
          </MIconButton>
        )
      });
    } catch (e) {
      enqueueSnackbar('A problem was encountered deleting the document.', {
        variant: 'error'
      });
    } finally {
      setLoading(false);
      setItemContext(null);
      getUploadedDocuments();
    }
  };

  const getUploadedDocuments = async () => {
    setLoading(true);
    try {
      const res = await callRestGetEndpoint(
        getQueryParams(customerAllData, sortOptions, pageSize, page, pageInfo),
        '/pos_customer/customer_upload_documents'
      );
      setDocuments(res?.data);
      setTotalCount(res?.totalCount);
      setPageInfo(res?.pageInfo);
    } catch (e) {
      enqueueSnackbar('A problem was encountered fetching documents.', {
        variant: 'error'
      });
    } finally {
      setLoading(false);
    }
  };

  React.useEffect(() => {
    getUploadedDocuments();
  }, [page, pageSize, sortOptions]);

  const sortChange = o => {
    if (o.length === 0) {
      const currentOptions = sortOptions[0];
      const nextOptions: GridSortModel = [
        {
          ...currentOptions,
          sort: sortOptions[0].sort === 'asc' ? 'desc' : 'asc'
        }
      ];
      setPage(0);
      setSortOptions(nextOptions);
    } else {
      setSortOptions(o);
    }
  };

  return (
    <>
      <div
        style={{
          display: 'flex',
          marginBottom: 16,
          justifyContent: 'flex-end'
        }}
      >
        <Button
          variant="contained"
          style={{ marginLeft: 10 }}
          color="primary"
          startIcon={<VisibilityIcon />}
          onClick={() => setUploadModalOpen(true)}
        >
          Upload Document
        </Button>
      </div>
      <RestoreDataGrid
        rowCount={totalCount}
        columnConfig={columnConfig}
        rows={documents}
        loading={loading}
        page={page}
        pageSize={pageSize}
        onPageChangeSize={setPageSize}
        onPageChange={setPage}
        sortModel={sortOptions}
        onSortModelChange={sortChange}
      />
      {uploadModalOpen && (
        <UploadFileModal
          customerId={customerAllData.id}
          closeModal={() => setUploadModalOpen(false)}
          uploadComplete={getUploadedDocuments}
        />
      )}
      {showConfirm && (
        <ConfirmationDialog
          open={showConfirm}
          variant="warning"
          title={'Confirm Delete'}
          onConfirm={() => {
            deleteFile();
            setShowConfirm(false);
          }}
          onClose={() => setShowConfirm(false)}
        >
          Are you sure you want to delete this document?
        </ConfirmationDialog>
      )}
      {viewDocumentOpen && (
        <UploadedDocumentView
          closeModal={() => {
            setViewDocumentOpen(false);
            setItemContext(undefined);
          }}
          documentId={itemContext}
        />
      )}
    </>
  );
};

export default UploadedDocuments;
